import { Fragment, useMemo } from "react";
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

import SysColumn from "app/class/sys-column";
import useTableColumns from "app/hooks/useTableColumns";
import { useTables } from "app/hooks/useTables";
import Translate from "component/global/translation/translation-component";
import { ConditionNode, ExistsNode } from "modules/record/record-producer/types/query.types";
import { ConditionRow } from "./ConditionRow";
import { AddConditionActions } from "./AddConditionActions";
import { createEmptyCondition } from "./utils/ast.utils";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";

interface ExistsRowProps {
    node: ExistsNode;
    baseColumns: SysColumn[];
    allColumns?: SysColumn[];
    tables?: any[];
    maxReferenceDepth?: number;
    onChange: (node: ExistsNode) => void;
    onRemove: () => void;
    onAddLogicalGroup?: (operator: 'and' | 'or') => void;
    onAddExists?: (operator?: 'and' | 'or') => void;
    isInNestedGroup?: boolean;
    formFields?: { name: string; label: string }[];
}

export const ExistsRow = ({
    node,
    baseColumns,
    allColumns,
    tables: providedTables,
    maxReferenceDepth,
    onChange,
    onRemove,
    onAddLogicalGroup,
    onAddExists,
    isInNestedGroup = false,
    formFields,
}: ExistsRowProps) => {
    const { t } = useTranslation();
    const { data: queriedTables = [] } = useTables({ enabled: !providedTables });
    const tables = providedTables || queriedTables;
    const selectedTable = useMemo(
        () => tables.find((table) => table.name === node.table),
        [node.table, tables]
    );
    const tableOptions = useMemo(
        () => [...tables]
            .filter((table) => table.name)
            .sort((left, right) => left.name.localeCompare(right.name)),
        [tables]
    );
    const existsColumns = useTableColumns(selectedTable?.id || "");

    const color = isInNestedGroup ? "#1976d2" : "primary.main";

    const updateCondition = (updatedCondition: ConditionNode) => {
        onChange({
            ...node,
            conditions: node.conditions.map((condition) =>
                condition.id === updatedCondition.id ? updatedCondition : condition
            ),
        });
    };

    const removeCondition = (conditionId: string) => {
        const remaining = node.conditions.filter((condition) => condition.id !== conditionId);

        onChange({
            ...node,
            conditions: remaining.length ? remaining : [createEmptyCondition() as ConditionNode],
        });
    };

    const addCondition = () => {
        onChange({
            ...node,
            conditions: [...node.conditions, createEmptyCondition() as ConditionNode],
        });
    };

    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
                sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                    bgcolor: "background.paper",
                }}
            >
                <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 48 }}>
                            EXISTS
                        </Typography>
                        <Autocomplete
                            size="small"
                            options={tableOptions}
                            value={selectedTable || null}
                            onChange={(_, table) =>
                                onChange({
                                    ...node,
                                    table: table?.name || "",
                                    conditions: node.conditions.length ? node.conditions : [createEmptyCondition() as ConditionNode],
                                })
                            }
                            getOptionLabel={(table) => table.name
                                ? `${translateMetadataLabel(t, table)} (${table.name})`
                                : ""
                            }
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            sx={{ minWidth: 220 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={String(t("Search table"))}
                                />
                            )}
                        />
                        <Tooltip title={t("Remove EXISTS condition")}>
                            <IconButton size="small" color="error" onClick={onRemove}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {node.table && !existsColumns.length && (
                        <Typography variant="caption" color="text.secondary">
                            <Translate text="No columns available for selected table." />
                        </Typography>
                    )}

                    {node.table && existsColumns.length > 0 && (
                        <Stack spacing={1} sx={{ pl: 1.5, borderLeft: "2px solid", borderColor: "divider" }}>
                            {node.conditions.map((condition, index) => (
                                <Fragment key={condition.id}>
                                    {index > 0 && (
                                        <Box sx={{ display: "flex", alignItems: "center", minHeight: 28 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    minWidth: 48,
                                                    height: 28,
                                                    position: "relative",
                                                    flexShrink: 0,
                                                    "&::before": {
                                                        content: '""',
                                                        position: "absolute",
                                                        left: "50%",
                                                        top: 0,
                                                        bottom: 0,
                                                        width: isInNestedGroup ? 3 : 2,
                                                        backgroundColor: color,
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        bgcolor: color,
                                                        color: "white",
                                                        px: 0.75,
                                                        py: 0.25,
                                                        borderRadius: 0.5,
                                                        fontWeight: "bold",
                                                        fontSize: "0.7rem",
                                                        zIndex: 1,
                                                        boxShadow: isInNestedGroup ? 1 : 0,
                                                    }}
                                                >
                                                    {t("AND")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                    <ConditionRow
                                        node={condition}
                                        columns={existsColumns}
                                        valueColumns={baseColumns}
                                        includeFieldOperators
                                        valueAllColumns={allColumns}
                                        valueTables={tables}
                                        maxReferenceDepth={maxReferenceDepth}
                                        formFields={formFields}
                                        onChange={updateCondition}
                                        onRemove={() => removeCondition(condition.id)}
                                    />
                                </Fragment>
                            ))}
                            <Box>
                                <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
                                    <Translate text="Add EXISTS condition" />
                                </Button>
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Box>
            <AddConditionActions
                onAddLogicalGroup={onAddLogicalGroup}
                onAddExists={onAddExists}
            />
        </Stack>
    );
};
